/**
 * RFC 9457 problem details handling.
 *
 * The backend returns `application/problem+json` for every error. This module
 * normalises those payloads (and non-conforming failures such as network
 * errors) into a single `ApiError` so UI code can render a consistent message.
 */

/** One field-level validation error from a 422 response. */
export interface FieldError {
  /** JSON pointer-ish location, e.g. `body.title`. */
  loc?: string[];
  /** Human readable message. */
  msg?: string;
  /** Machine readable code. */
  type?: string;
  /** Backend problem field name. */
  field?: string;
  /** Backend problem message. */
  message?: string;
}

/** Shape of the problem+json body emitted by the API. */
export interface Problem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  errors?: FieldError[];
  [key: string]: unknown;
}

/** Error raised for any failed API call. */
export class ApiError extends Error {
  /** HTTP status (0 for network failures). */
  readonly status: number;
  /** Machine readable error code, when provided. */
  readonly code: string | undefined;
  /** Field errors for validation failures. */
  readonly errors: FieldError[];
  /** The raw problem payload. */
  readonly problem: Problem;

  constructor(status: number, problem: Problem) {
    super(problem.detail ?? problem.title ?? `Request failed (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = problem.code;
    this.errors = problem.errors ?? [];
    this.problem = problem;
  }

  /** Field errors keyed by the last path segment (e.g. `title`). */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.errors) {
      const key = e.loc?.at(-1) ?? e.field ?? "_";
      out[key] = e.msg ?? e.message ?? "";
    }
    return out;
  }
}

/**
 * Convert an `openapi-fetch` result into data or throw an `ApiError`.
 *
 * Usage: `const data = unwrap(await api.GET("/api/v1/me"))`.
 */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.response.ok) {
    return result.data as T;
  }
  const problem = isProblem(result.error)
    ? result.error
    : { detail: typeof result.error === "string" ? result.error : "" };
  throw new ApiError(result.response.status, problem);
}

/** Type guard for problem+json payloads. */
export function isProblem(value: unknown): value is Problem {
  return typeof value === "object" && value !== null;
}

/** Return a user-facing message for any thrown value. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}
